import * as React from "react"
import { router } from "@inertiajs/react"
import { Download, RefreshCw, Send, Sparkles, Trash2 } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { InlineField } from "@/components/ui/inline-edit"
import { StatusBadge } from "@/components/studio/StatusBadge"
import { useInlineRecord } from "@/hooks/use-inline-record"
import { inlinePatch } from "@/lib/inline-save"
import {
  MEDIA_TYPE_LABELS,
  formatCost,
  type MediaType,
  type StudioAsset,
  type StudioCapabilities,
} from "@/components/studio/types"

const TEXTAREA_CLASS =
  "w-full rounded-md border border-hairline bg-page px-3 py-2 text-sm text-ink-body placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-accent"

const GENERATE_HINTS: Record<MediaType, string> = {
  social_post: "Claude writes a caption and hashtags from the article.",
  poster: "A shareable image with the article's key message rendered on it.",
  ad_creative: "One organic-style ad image with headline and copy.",
  cartoon_video: "A short cartoon clip — drafted first, generated only after your approval.",
}

function MediaPreview({ asset }: { asset: StudioAsset }) {
  if (!asset.media_url) return null
  if (asset.media_type === "cartoon_video") {
    return (
      <video controls className="mt-3 w-full max-w-sm rounded-md border border-hairline" src={asset.media_url} />
    )
  }
  return (
    <img
      src={asset.media_url}
      alt={asset.copy.overlay_text ?? `${MEDIA_TYPE_LABELS[asset.media_type]} preview`}
      className="mt-3 w-full max-w-sm rounded-md border border-hairline"
    />
  )
}

function CaptionEditor({ asset }: { asset: StudioAsset }) {
  const fields = useInlineRecord({
    record: { caption: asset.copy.caption ?? "" },
    save: (_key, value) =>
      inlinePatch(`/admin/studio/assets/${asset.id}`, {
        content_asset: { copy: { caption: value } },
      }),
  })
  const captionState = fields.state("caption")

  return (
    <InlineField label="Caption" htmlFor={`caption-${asset.id}`} state={captionState} className="mt-3">
      <textarea
        id={`caption-${asset.id}`}
        rows={3}
        value={captionState.value}
        onChange={(e) => fields.set("caption", e.target.value)}
        onKeyDown={captionState.multilineKeys}
        className={`mt-1 ${TEXTAREA_CLASS}`}
        placeholder="The caption posted alongside this media…"
      />
    </InlineField>
  )
}

function ApproveDialog({ asset }: { asset: StudioAsset }) {
  const [open, setOpen] = React.useState(false)

  function approve() {
    setOpen(false)
    router.patch(`/admin/studio/assets/${asset.id}/approve`, {}, { preserveScroll: true })
  }

  return (
    <>
      <Button type="button" size="sm" onClick={() => setOpen(true)} disabled={!asset.prompt}>
        <Sparkles className="mr-1.5 h-3.5 w-3.5" />
        Approve &amp; generate ({formatCost(asset.cost_estimate_cents)})
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate this video?</DialogTitle>
            <DialogDescription>
              Approving triggers the paid video generation — roughly {formatCost(asset.cost_estimate_cents)} (approx., provider pricing moves).
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-48 overflow-y-auto rounded-md border border-hairline bg-page p-3 text-sm text-ink-body">
            {asset.prompt}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={approve}>
              Approve — generate now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export function AssetCard({
  mediaType,
  asset,
  articleId,
  capabilities,
  autoPublish,
}: {
  mediaType: MediaType
  asset: StudioAsset | null
  articleId: number
  capabilities: StudioCapabilities
  autoPublish: boolean
}) {
  const needsMedia = mediaType !== "social_post"
  const canGenerate = capabilities.ai_enabled && (!needsMedia || capabilities.media_enabled)

  function generate() {
    router.post(
      "/admin/studio/assets",
      { article_id: articleId, media_type: mediaType },
      { preserveScroll: true },
    )
  }

  function act(action: "regenerate" | "publish") {
    router.patch(`/admin/studio/assets/${asset!.id}/${action}`, {}, { preserveScroll: true })
  }

  function destroy() {
    router.delete(`/admin/studio/assets/${asset!.id}`, { preserveScroll: true })
  }

  return (
    <div className="rounded-lg border border-hairline bg-surface p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3>{MEDIA_TYPE_LABELS[mediaType]}</h3>
        {asset ? <StatusBadge status={asset.status} /> : <Badge tone="muted">Not created</Badge>}
      </div>

      {!asset && (
        <div className="mt-3">
          <p className="text-sm text-ink-muted">{GENERATE_HINTS[mediaType]}</p>
          <Button
            type="button"
            size="sm"
            className="mt-3"
            onClick={generate}
            disabled={!canGenerate}
            title={
              canGenerate
                ? undefined
                : !capabilities.ai_enabled
                  ? "Set ANTHROPIC_API_KEY to enable AI drafting"
                  : "Set FAL_KEY to enable image and video generation"
            }
          >
            <Sparkles className="mr-1.5 h-3.5 w-3.5" />
            {mediaType === "cartoon_video" ? "Request video brief" : "Generate"}
          </Button>
        </div>
      )}

      {asset && (
        <>
          {asset.status === "awaiting_approval" && (
            <div className="mt-3">
              {asset.prompt ? (
                <>
                  <p className="text-sm text-ink-muted">Video prompt drafted — review it, then approve to run the paid generation.</p>
                  <div className="mt-2 max-h-32 overflow-y-auto rounded-md border border-hairline bg-page p-3 text-sm text-ink-body">
                    {asset.prompt}
                  </div>
                  <div className="mt-3">
                    <ApproveDialog asset={asset} />
                  </div>
                </>
              ) : (
                <p className="text-sm text-ink-muted">Drafting the video prompt…</p>
              )}
            </div>
          )}

          {(asset.status === "queued" || asset.status === "generating") && (
            <p className="mt-3 text-sm text-ink-muted">
              {asset.status === "queued" && asset.automation_source === "nightly"
                ? "Queued for tonight's batch."
                : "Working on it — this updates automatically."}
            </p>
          )}

          {asset.status === "failed" && (
            <p className="mt-3 text-sm text-ink-muted">{asset.error_message ?? "Generation failed."}</p>
          )}

          {(asset.status === "ready" || asset.status === "published") && (
            <>
              <MediaPreview asset={asset} />
              {asset.copy.hashtags && asset.copy.hashtags.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {asset.copy.hashtags.map((tag) => (
                    <Badge key={tag} tone="neutral">
                      #{tag.replace(/^#/, "")}
                    </Badge>
                  ))}
                </div>
              )}
              {asset.copy.headline && (
                <p className="mt-2 text-sm text-ink-body">
                  <strong>{asset.copy.headline}</strong>
                  {asset.copy.primary_text ? ` — ${asset.copy.primary_text}` : ""}
                </p>
              )}
              {asset.status === "ready" ? (
                <CaptionEditor asset={asset} />
              ) : (
                asset.copy.caption && <p className="mt-3 text-sm text-ink-body">{asset.copy.caption}</p>
              )}
              {asset.status === "ready" && asset.error_message && (
                <p className="mt-2 text-sm text-ink-muted">Publish failed: {asset.error_message}</p>
              )}
            </>
          )}

          {asset.status === "published" && (
            <p className="mt-2 text-xs text-ink-muted">
              Posted to Meta
              {asset.meta_post_ids.instagram ? " (Facebook + Instagram)" : " (Facebook)"}
              {asset.published_at && ` · ${new Date(asset.published_at).toLocaleDateString("en-GB")}`}
            </p>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            {asset.status === "ready" &&
              (capabilities.meta_enabled ? (
                <Button type="button" size="sm" onClick={() => act("publish")}>
                  <Send className="mr-1.5 h-3.5 w-3.5" />
                  {asset.error_message ? "Retry publish" : "Publish to Meta"}
                </Button>
              ) : (
                <Button type="button" size="sm" disabled title="Set META_ACCESS_TOKEN and META_PAGE_ID to enable publishing">
                  <Send className="mr-1.5 h-3.5 w-3.5" />
                  Publish to Meta
                </Button>
              ))}
            {asset.status === "ready" && autoPublish && capabilities.meta_enabled && !asset.error_message && (
              <Badge tone="accent">Auto-publish is on</Badge>
            )}
            {asset.download_url && (
              <Button type="button" size="sm" variant="secondary" asChild>
                <a href={asset.download_url} download className="no-underline">
                  <Download className="mr-1.5 h-3.5 w-3.5" />
                  Download
                </a>
              </Button>
            )}
            {(asset.status === "ready" || asset.status === "failed" || asset.status === "published") && (
              <Button type="button" size="sm" variant="ghost" onClick={() => act("regenerate")}>
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                Regenerate
              </Button>
            )}
            {(asset.status === "failed" || asset.status === "ready") && (
              <Button type="button" size="sm" variant="ghost" onClick={destroy}>
                <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                Remove
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  )
}
